/**
 * The expanded floating panel (spec sections 2, 4, 8 and 9). Non-modal: `role="dialog"` with no
 * `aria-modal` and no focus trap, portalled to `body`. Existing tool shortcut guards stand down for
 * `[role="dialog"]`, and the key isolation on the root keeps everything else (Esc, F5...) inside it.
 */
import { useCallback, useState, type ClipboardEvent, type DragEvent, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import { TooltipProvider } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { dragHasFiles, imagesFromClipboard, imagesFromDrop } from './clipboardImages';
import { FEEDBACK_LAYER_CLASS, MAX_IMAGES, anchorStyle, panelMaxHeight } from './constants';
import {
  announce,
  closePanel,
  getFeedbackState,
  hasDraft,
  minimize,
  removeImage,
  requestFocus,
  rememberFocus,
  setCapture,
  setCategory,
  setCorner,
  setNote,
  setSeverity,
  addImagePlaceholders,
  resolveImage,
  clearDraft,
  type FeedbackState,
} from './feedbackStore';
import { isolateKeys } from './isolateKeys';
import { placeholderImage, planFiles, processImage } from './images';
import { NoteAndFields } from './NoteAndFields';
import { PanelFooter } from './PanelFooter';
import { PanelHeader } from './PanelHeader';
import { ScreenshotSection } from './ScreenshotSection';
import { SelectionSection } from './SelectionSection';
import type { PickMode } from './usePickController';
import { usePanelDrag } from './usePanelDrag';
import type { SendOutcome } from './useSendFeedback';

interface Props {
  state: FeedbackState;
  onPick: (mode: PickMode) => void;
  onSend: () => Promise<SendOutcome>;
}

export function FeedbackPanel({ state, onPick, onSend }: Props) {
  const [noteError, setNoteError] = useState(false);
  const [pasteError, setPasteError] = useState<string | null>(null);
  const [hot, setHot] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const drag = usePanelDrag<HTMLDivElement>({ onSnap: setCorner });
  const { sending, unavailable, saveError } = state;
  const picking = state.view === 'picking';

  const attach = useCallback(async (files: File[], pasted: boolean): Promise<void> => {
    const plan = planFiles(files, getFeedbackState().images.length);
    const errors = [...plan.errors];
    setPasteError(errors.length > 0 ? errors.join(' ') : null);
    const entries = plan.files.flatMap((file) => {
      const label = pasted ? 'Pasted image' : file.name || 'Image';
      const placeholder = placeholderImage(file, crypto.randomUUID(), label);
      return placeholder ? [{ file, placeholder }] : [];
    });
    addImagePlaceholders(entries.map((e) => e.placeholder));
    await Promise.all(
      entries.map(async ({ file, placeholder }) => {
        const result = await processImage(file, placeholder.id, placeholder.name);
        if ('image' in result) {
          resolveImage(placeholder.id, result.image);
          announce(`Screenshot added, ${getFeedbackState().images.filter((i) => i.blob).length} of ${MAX_IMAGES}.`);
        } else {
          resolveImage(placeholder.id, null);
          errors.push(result.error);
        }
      }),
    );
    setPasteError(errors.length > 0 ? errors.join(' ') : null);
  }, []);

  const handlePaste = (event: ClipboardEvent): void => {
    const files = imagesFromClipboard(event.clipboardData);
    if (files.length === 0) return; // text paste: leave it to the browser
    event.preventDefault();
    void attach(files, true);
  };

  const handleDragOver = (event: DragEvent): void => {
    if (!dragHasFiles(event.dataTransfer)) return;
    event.preventDefault();
    setHot(true);
  };

  const handleDrop = (event: DragEvent): void => {
    setHot(false);
    const files = imagesFromDrop(event.dataTransfer);
    if (!dragHasFiles(event.dataTransfer)) return;
    event.preventDefault();
    if (files.length > 0) void attach(files, false);
  };

  const keepEditing = (): void => {
    setConfirmDiscard(false);
    requestFocus('feedback-close');
  };

  const handleClose = (): void => {
    if (unavailable || !hasDraft(getFeedbackState())) {
      closePanel();
      return;
    }
    setConfirmDiscard(true);
    requestFocus('feedback-keep-editing');
  };

  const handleSend = async (): Promise<void> => {
    const outcome = await onSend();
    if (outcome === 'invalid') {
      setNoteError(true);
      requestFocus('feedback-note');
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Escape' && !event.defaultPrevented) {
      if (confirmDiscard) keepEditing();
      else minimize();
    } else if (
      event.key === 'Enter' &&
      (event.ctrlKey || event.metaKey) &&
      (event.target as HTMLElement).id === 'feedback-note'
    ) {
      event.preventDefault();
      void handleSend();
    }
    // Never preventDefault here beyond the above: typing, Tab, Ctrl+V and F5 keep their native behaviour.
    event.stopPropagation();
  };

  const removeThumb = (id: string, index: number): void => {
    const before = getFeedbackState().images;
    removeImage(id);
    announce(`Screenshot ${index + 1} removed.`);
    const remaining = before.filter((i) => i.id !== id);
    // Focus the thumb that slid into this slot, else the one before it, else the paste zone.
    let next: number | null = null;
    if (remaining[index]) next = index + 1;
    else if (remaining[index - 1]) next = index;
    requestFocus(next === null ? 'feedback-paste-zone' : `feedback-thumb-remove-${next}`);
  };

  const copyNote = (): void => {
    void navigator.clipboard?.writeText(getFeedbackState().note).then(() => announce('Note copied.'));
  };

  return createPortal(
    <TooltipProvider delayDuration={600}>
      <div
        ref={drag.ref}
        id="feedback-panel"
        role="dialog"
        aria-labelledby="feedback-panel-title"
        data-feedback-panel=""
        data-testid="feedback-panel"
        tabIndex={-1}
        hidden={picking}
        style={{ ...anchorStyle(state.corner), maxHeight: panelMaxHeight(), ...drag.style }}
        className={cn(
          'fixed flex w-[min(--spacing(90),calc(100vw-(--spacing(6))))] flex-col rounded-lg border border-border bg-card text-card-foreground shadow-[var(--shadow-popover)] transition-[translate] duration-150 motion-reduce:transition-none',
          FEEDBACK_LAYER_CLASS,
        )}
        onKeyDown={handleKeyDown}
        onKeyUp={isolateKeys.onKeyUp}
        onKeyPress={isolateKeys.onKeyPress}
        onFocus={(e) => rememberFocus((e.target as HTMLElement).dataset.testid ?? null)}
        onPaste={handlePaste}
        onDragOver={handleDragOver}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setHot(false);
        }}
        onDrop={handleDrop}
      >
        <PanelHeader
          corner={state.corner}
          sending={sending}
          dragging={drag.dragging}
          dragHandlers={drag.handlers}
          onCorner={setCorner}
          onMinimize={minimize}
          onClose={handleClose}
        />
        <div aria-busy={sending || undefined} className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-3">
          {unavailable ? (
            <Empty data-testid="feedback-unavailable">
              <EmptyHeader>
                <EmptyTitle>Feedback isn&apos;t available here</EmptyTitle>
                <EmptyDescription>This environment no longer accepts feedback. Your note was not sent.</EmptyDescription>
              </EmptyHeader>
              <EmptyContent>
                <Button type="button" variant="outline" size="sm" data-testid="feedback-copy-note" onClick={copyNote}>
                  Copy note
                </Button>
              </EmptyContent>
            </Empty>
          ) : (
            <>
              <SelectionSection
                capture={state.capture}
                disabled={sending}
                onPick={onPick}
                onClear={() => {
                  setCapture(null);
                  announce('Selection cleared.');
                }}
              />
              <ScreenshotSection
                images={state.images}
                hot={hot}
                pasteError={pasteError}
                disabled={sending}
                onRemove={removeThumb}
              />
              <NoteAndFields
                note={state.note}
                category={state.category}
                severity={state.severity}
                noteError={noteError && state.note.trim().length === 0}
                readOnly={sending}
                onNote={(note) => {
                  setNote(note);
                  if (note.trim().length > 0) setNoteError(false);
                }}
                onCategory={setCategory}
                onSeverity={setSeverity}
              />
              {saveError && (
                <Alert variant="destructive" data-testid="feedback-error">
                  <AlertTitle>Not sent</AlertTitle>
                  <AlertDescription>{saveError.description}</AlertDescription>
                </Alert>
              )}
            </>
          )}
        </div>
        {!unavailable && (
          <PanelFooter
            sending={sending}
            progress={state.progress}
            canSend={saveError?.kind !== 'forbidden'}
            confirmDiscard={confirmDiscard}
            onSend={() => void handleSend()}
            onKeepEditing={keepEditing}
            onDiscard={() => {
              setConfirmDiscard(false);
              clearDraft();
              closePanel();
            }}
          />
        )}
        <div role="status" aria-live="polite" data-testid="feedback-live" className="sr-only">
          {state.announcement}
        </div>
      </div>
    </TooltipProvider>,
    document.body,
  );
}
